import { useQueries } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import type { RootState } from "~/app/store";
import { selectIsCartAboveThreshold } from "../redux/selectors/cartSelectors";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { buildProductDescriptor } from "~/domains/pna/descriptor";
import { toAvailabilityOutcomeFromQueryState, toOutcomeFromQueryState } from "~/domains/pna/pnaSnapshot";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import { selectLineItemsByProduct } from "~/features/cart/cart/redux/selectors/cartSelectors";
import type { PlaceOrderEntrySnapshot } from "~/features/cart/cart/redux/actions/checkoutPageActions";
import {
  buildCartPnaLoad,
  cartPnaPairKey,
  cartPnaQueryOptions,
  quotePackCodeFromEntries,
  type CartDescriptorFor,
} from "../pna/cartPna";
import { cachedActiveCartResourceId } from "~/domains/cart/utils/cartResourceIdentity";

export interface CartPnaQueries {
  /** Descriptors keyed by {@link cartPnaPairKey} — one per distinct (productCode, priceReference) pair. */
  byPair: Map<string, ProductDescriptor>;
  /** Descriptor lookup for view-model table builders; unsubscribed pairs project pending. */
  descriptorFor: CartDescriptorFor;
  /** Some cart entry's PnA is still in flight (replaces the legacy `selectIsPnaLoading`). */
  isPnaLoading: boolean;
  /** Some cart entry has no loaded PnA yet (the "Load all PnA" visibility gate). */
  someNotLoaded: boolean;
  /** Some cart entry is neither loaded nor loading (the "Load all PnA" enable gate). */
  someNeedsLoading: boolean;
  /** placeOrder analytics snapshots, kept current with the queries (A07). */
  entrySnapshots: PlaceOrderEntrySnapshot[];
}

/**
 * Cart-wide PnA LIST hook (A07): subscribes to the canonical price/availability queries for
 * every distinct (productCode, priceReference, packCode) triple in the cart and projects them
 * into per-pair descriptors. The orchestration thunks load through the same queries, so this
 * hook observes exactly the data the cart load published.
 *
 * The subscription is threshold-gated exactly like the page-load orchestration: above
 * `cartPnaThreshold` the queries stay disabled (zero transport). Imperative loads (`fetchQuery`)
 * ignore `enabled`, and these disabled observers still receive their cache updates.
 */
export function useCartPnaQueries(): CartPnaQueries {
  const lineItemsByProduct = useSelector(selectLineItemsByProduct, shallowEqual);

  const subscriptions = useMemo(() => {
    const seen = new Set<string>();
    const list: { productCode: string; priceReference: string; packCode?: string }[] = [];
    Object.values(lineItemsByProduct).forEach((lineItems: LineItem[]) => {
      if (lineItems.length === 0) {
        return;
      }
      const productCode = lineItems[0].productCode;
      const packCode = quotePackCodeFromEntries(lineItems);
      new Set(lineItems.map((lineItem) => lineItem.priceReference)).forEach((priceReference) => {
        const key = cartPnaPairKey(productCode, priceReference);
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        list.push({ productCode, priceReference, packCode });
      });
    });
    return list;
  }, [lineItemsByProduct]);

  const codes = useMemo(() => [...new Set(subscriptions.map((s) => s.productCode))], [subscriptions]);
  const bases = useSelector((state: RootState) => codes.map((code) => state.products.basic[code]), shallowEqual);
  const baseByCode = useMemo(() => new Map(codes.map((code, index) => [code, bases[index]])), [codes, bases]);

  // Uses the same entry-count measure as selectIsCartAboveThreshold / cartPageLoadThunk.
  const isAboveThreshold = useSelector(selectIsCartAboveThreshold);
  const pnaGate = !isAboveThreshold;

  const subscriptionsWithLoads = subscriptions.map((s) =>
    buildCartPnaLoad({ ...s, base: baseByCode.get(s.productCode), activeCartId: cachedActiveCartResourceId() }),
  );

  const gatedOptions = <T extends { enabled?: unknown }>(options: T): T =>
    pnaGate ? options : ({ ...options, enabled: false } as T);

  const priceQueries = useQueries({
    queries: subscriptionsWithLoads.map((load) => gatedOptions(cartPnaQueryOptions(load).price)),
  });
  const availabilityQueries = useQueries({
    queries: subscriptionsWithLoads.map((load) => gatedOptions(cartPnaQueryOptions(load).availability)),
  });

  const priceSignature = priceQueries
    .map((query) => [query.fetchStatus, query.dataUpdatedAt, query.errorUpdateCount].join(":"))
    .join("|");
  const availabilitySignature = availabilityQueries
    .map((query) => [query.fetchStatus, query.dataUpdatedAt, query.errorUpdateCount].join(":"))
    .join("|");

  // Descriptors recompute only when a per-query state signature changes — the query results
  // themselves get fresh identities every render, which would churn every memo downstream.
  // State-based projection: gated-but-idle pairs are dictionary misses (not loading), while
  // already-cached pairs keep showing their data.
  const descriptors = useMemo(
    () =>
      subscriptions.map((s, index) =>
        buildProductDescriptor(
          baseByCode.get(s.productCode),
          {
            price: toOutcomeFromQueryState(priceQueries[index]),
            availability: toAvailabilityOutcomeFromQueryState(availabilityQueries[index]),
          },
          s.priceReference,
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- priceQueries/availabilityQueries are covered by their signatures above
    [subscriptions, baseByCode, priceSignature, availabilitySignature],
  );

  const byPair = useMemo(() => {
    const map = new Map<string, ProductDescriptor>();
    subscriptions.forEach((s, index) => map.set(cartPnaPairKey(s.productCode, s.priceReference), descriptors[index]));
    return map;
  }, [subscriptions, descriptors]);

  const descriptorFor = useCallback<CartDescriptorFor>(
    (productCode, priceReference) =>
      byPair.get(cartPnaPairKey(productCode, priceReference)) ??
      buildProductDescriptor(
        undefined,
        { price: { status: "pending" }, availability: { status: "pending" } },
        priceReference,
      ),
    [byPair],
  );

  const entrySnapshots = useMemo(() => {
    const lineItems = Object.values(lineItemsByProduct).flat();
    return lineItems.map((lineItem) => ({
      lineItem,
      product: descriptorFor(lineItem.productCode, lineItem.priceReference),
    }));
  }, [lineItemsByProduct, descriptorFor]);

  return {
    byPair,
    descriptorFor,
    isPnaLoading: descriptors.some((descriptor) => descriptor.isLoading),
    someNotLoaded: descriptors.some((descriptor) => !descriptor.isLoaded),
    someNeedsLoading: descriptors.some((descriptor) => !descriptor.isLoaded && !descriptor.isLoading),
    entrySnapshots,
  };
}
